import React from 'react';
import { List } from 'immutable';
import { GridList } from 'react-aria-components';

// Components
import CreateLabelListItem from './CreateLabelListItem';
import EmptyResultsListItem from './EmptyResultsListItem';
import LabelListSection from './LabelListSection';
import useLabelListData from '../hooks/useLabelListData';
import useLabelListSelection from '../hooks/useLabelListSelection';

// Utils
import { useTranslator } from '../../../../../../components/i18n/useTranslator';

// Types
import { Label } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import LabelListEmptyPlaceholder from './LabelListEmptyPlaceholder';

type Props = {
    handleToggleLabelSelection: (labelId: string) => void;
    handleCreateLabel: () => void;
    elements: List<ImMNElement>;
    searchString: string;
    allLabels: Label[];
    handleClickEdit?: (label: Label) => void;
    isSheet?: boolean;
    size?: 'sm' | 'lg';
};

const LabelLists = (props: Props) => {
    const {
        elements,
        searchString,
        allLabels,
        handleToggleLabelSelection,
        handleCreateLabel,
        handleClickEdit,
        isSheet = false,
        size = 'sm',
    } = props;
    const { allLabelsFiltered, suggestedLabels, hasVisibleLabels, isSearching } = useLabelListData({
        allLabels,
        searchString,
    });

    const { checkedLabelIds, editingLabelId, gridListInteractionProps, handleEditingChange, handleSelectionChange } =
        useLabelListSelection({
            elements,
            handleCreateLabel,
            handleToggleLabelSelection,
        });
    const translate = useTranslator('popup', 'labels.menu');
    const shouldShowEmptyPlaceholder = !hasVisibleLabels && !isSearching;

    if (shouldShowEmptyPlaceholder) {
        return (
            <div className="LabelLists">
                <LabelListEmptyPlaceholder />
            </div>
        );
    }

    return (
        <GridList
            {...gridListInteractionProps}
            aria-label="Labels"
            selectedKeys={checkedLabelIds}
            onSelectionChange={handleSelectionChange}
            className="LabelLists w-full min-w-0 outline-hidden"
        >
            <CreateLabelListItem searchString={searchString} size={size} />

            {hasVisibleLabels && isSearching && (
                <LabelListSection
                    checkedLabelIds={checkedLabelIds}
                    editingLabelId={editingLabelId}
                    isSheet={isSheet}
                    labels={allLabelsFiltered}
                    onEditingChange={handleEditingChange}
                    handleClickEdit={handleClickEdit}
                    sectionId="search-results-section"
                    showSeparator={true}
                    size={size}
                />
            )}

            {hasVisibleLabels &&
                !isSearching &&
                suggestedLabels.map(({ id, title, labels }, index) => (
                    <LabelListSection
                        key={id}
                        checkedLabelIds={checkedLabelIds}
                        editingLabelId={editingLabelId}
                        header={title}
                        isSheet={isSheet}
                        labels={labels}
                        onEditingChange={handleEditingChange}
                        sectionClassName={index === 0 && suggestedLabels.length > 1 ? 'pb-2' : undefined}
                        sectionId={`${id}-section`}
                        showSeparator={index > 0}
                        handleClickEdit={handleClickEdit}
                        size={size}
                    />
                ))}

            {!hasVisibleLabels && isSearching && <EmptyResultsListItem text={translate('noResults')} />}
        </GridList>
    );
};

export default LabelLists;
